<template>
  <div class="flow-menu">
    <item-header type="flow" @close="$emit('close')" :button-centered="true">
      <editable-text
        ref="titleInput"
        :text="ctx.flow.title"
        :displayed-text="ctx.title || $t('labels.unnamed')"
        :editable="true"
        :single-line="false"
        class="itemheader__title"
        @update:text="ctx.updateKey('title', $event)"
      />
    </item-header>

    <!-- GENERAL OPTIONS -->
    <drawer-block
      v-model:open="drawerStates.flowGeneral"
      :title="$t('labels.general')"
    >
      <div class="column gap-4">
        <input-text
          ref="titleField"
          :label="$t('labels.title')"
          :value="ctx.flow.title"
          @enter="descriptionField?.focus()"
          @update:value="ctx.updateKey('title', $event)"
        />

        <input-texteditor
          ref="descriptionField"
          :label="$t('labels.description')"
          :value="ctx.flow.description"
          @update:value="ctx.updateKey('description', $event)"
        />
      </div>
    </drawer-block>

    <drawer-block
      v-model:open="drawerStates.flowValue"
      :title="$t('labels.flowValue')"
    >
      <div class="column">
        <input-state-toggle
          :states="['expression', 'reference']"
          :value="ctx.flow.valueInputMode"
          v-slot="{ state }"
          @update:value="ctx.updateKey('valueInputMode', $event)"
        >
          <span>{{ $t(`valueInputType.${state}`) }}</span>
        </input-state-toggle>

        <template v-if="ctx.flow.valueInputMode == 'expression'">
          <expression-input
            :expression="ctx.flow.valueExpression"
            :parse-context="{
              affectedType: 'flow',
              affectedId: ctx.flow.id
            }"
            @update:expression="ctx.updateKey('valueExpression', $event)"
          />
        </template>
        <template v-if="ctx.flow.valueInputMode == 'reference'">
          <input-dropdown
            :options="itemsByUid"
            :value="ctx.referencedElement"
            :label="$t('labels.controlElement')"
            :searchable="true"
            prop="title"
            item-key="uid"
            group-key="type"
            @update:value="setReferencedElement($event)"
          >
            <template #default="{ item }">
              <span v-if="item.title">{{ item.title }}</span>
              <span v-else class="placeholder-text">
                {{ $t("labels.unnamed") }}
              </span>
            </template>
            <template #grouplabel="{ value: itemType }">
              <div class="options-list__grouplabel">
                {{ $t(`objectNames.${itemType}`, 3) }}
              </div>
            </template>
          </input-dropdown>
        </template>

        <div class="pv-2">
          <effect-expression
            v-slot="{ value, unit }"
            :expression="ctx.expression"
            :parse-context="{
              targets: targetStore.items,
              resources: resourceStore.items,
              indices: indexStore.items
            }"
            number-format="value"
          >
            = <strong>{{ value }}</strong>
            {{ unit }}&thinsp;/&thinsp;{{ effectiveRoundLength > 1 ? `${$n(effectiveRoundLength)}&thinsp;` : "" }}{{ $t(`roundUnits.${roundUnit}`, effectiveRoundLength) }}
          </effect-expression>
        </div>
      </div>
    </drawer-block>

    <drawer-block :title="$t('labels.flowBranchElements')" :open="true">
      <div class="column">
        <div>
          <h3 class="label mt-0">
            {{ $t("words.source", ctx.sourceFlowBranches.length) }}
          </h3>
          <sortable-list
            :items="ctx.sourceFlowBranches"
            class="flow-menu__branches"
            :line-class="['flow-menu__branch']"
            :group="{
              id: `flow-branches-${flowId}`,
              branchType: 'source' as const
            }"
            gap="0.5rem"
            @move-list="
              updateFlowBranchDirections(
                $event.items.map(i => i.id),
                $event.targetList.branchType,
                $event.targetIndex
              )
            "
          >
            <template #default="{ item: branch }">
              <button-item
                @click.prevent="selectBranch(branch)"
                :selected="branch.id == selectedBranch?.id"
              >
                <span v-if="itemsByUid[`${branch.elementType}_${branch.elementId}`]?.title">
                  {{
                    itemsByUid[`${branch.elementType}_${branch.elementId}`].title
                  }}
                </span>
                <span v-else class="placeholder-text">
                  {{ $t("labels.unnamed") }}
                </span>
              </button-item>
              <span>
                {{ $n(ctx.branchValues[branch.id] || 0, "displaySign") }}
                {{ getElementUnit(flowBranchStore.elements[branch.id]) }}
              </span>
              <button
                class="button-icon"
                type="button"
                :title="branch.limitsFlow ? $t('labels.sourceBranchFlowLimited') : $t('labels.sourceBranchFlowUnlimited')"
                @click.prevent="selectBranch(branch)"
              >
                <lucide-icon :name="branch.limitsFlow ? 'arrow-right-from-line': 'arrow-right'" />
              </button>
              <button
                class="button-icon"
                type="button"
                @click.prevent="deleteBranch(branch)"
              >
                <lucide-icon name="trash-2" />
              </button>
            </template>

            <template #after-list>
              <p v-if="ctx.sourceFlowBranches.length == 0">
                {{ $t("texts.fromOutside") }}
              </p>
            </template>
          </sortable-list>
        </div>

        <div>
          <h3 class="label mt-0">
            {{ $t("words.sink", ctx.sinkFlowBranches.length) }}
          </h3>
          <sortable-list
            :items="ctx.sinkFlowBranches"
            class="flow-menu__branches"
            :line-class="['flow-menu__branch']"
            :group="{
              id: `flow-branches-${flowId}`,
              branchType: 'sink' as const
            }"
            gap="0.5rem"
            @move-list="
              updateFlowBranchDirections(
                $event.items.map(i => i.id),
                $event.targetList.branchType,
                $event.targetIndex
              )
            "
          >
            <template #default="{ item: branch }">
              <button-item
                @click.prevent="selectBranch(branch)"
                :selected="branch.id == selectedBranch?.id"
              >
                <span v-if="itemsByUid[`${branch.elementType}_${branch.elementId}`]?.title">
                  {{
                    itemsByUid[`${branch.elementType}_${branch.elementId}`].title
                  }}
                </span>
                <span v-else class="placeholder-text">
                  {{ $t("labels.unnamed") }}
                </span>
              </button-item>
              <span>
                {{ $n(ctx.branchValues[branch.id] || 0, "displaySign") }}
                {{ getElementUnit(flowBranchStore.elements[branch.id]) }}
              </span>
              <button
                class="button-icon"
                type="button"
                :title="branch.limitsFlow ? $t('labels.sinkBranchFlowLimited') : $t('labels.sinkBranchFlowUnlimited')"
                @click.prevent="selectBranch(branch)"
              >
                <lucide-icon :name="branch.limitsFlow ? 'arrow-right-to-line' : 'arrow-right'" />
              </button>
              <button
                class="button-icon"
                type="button"
                @click.prevent="deleteBranch(branch)"
              >
                <lucide-icon name="trash-2" />
              </button>
            </template>

            <template #after-list>
              <p v-if="ctx.sinkFlowBranches.length == 0">
                {{ $t("texts.toOutside") }}
              </p>
            </template>
          </sortable-list>
        </div>

        <div>
          <div class="line pb-2 pt-6">
            <button
              class="button button--clear"
              @click="
                () => {
                  sourceInputVisible = !sourceInputVisible;
                  sinkInputVisible = false;
                }
              "
            >
              {{ $t("actions.createSource") }}
            </button>
            <button
              class="button button--clear"
              @click="
                () => {
                  sinkInputVisible = !sinkInputVisible;
                  sourceInputVisible = false;
                }
              "
            >
              {{ $t("actions.createSink") }}
            </button>
          </div>

          <input-dropdown
            v-if="sinkInputVisible"
            style="grid-column: 1 / span 4"
            :options="availableBranchElements"
            prop="title"
            group-key="type"
            :placeholder="`Neue Senke...`"
            :clear-on-select="true"
            :searchable="true"
            @update:value="addBranch($event, 'sink')"
          >
            <template #grouplabel="{ value: itemType }">
              <div class="options-list__grouplabel">
                {{ $t(`objectNames.${itemType}`, 3) }}
              </div>
            </template>
          </input-dropdown>

          <input-dropdown
            v-if="sourceInputVisible"
            style="grid-column: 1 / span 4"
            :options="availableBranchElements"
            prop="title"
            group-key="type"
            :searchable="true"
            :placeholder="`Neue Quelle...`"
            :clear-on-select="true"
            @update:value="addBranch($event, 'source')"
          >
            <template #grouplabel="{ value: itemType }">
              <div class="options-list__grouplabel">
                {{ $t(`objectNames.${itemType}`, 3) }}
              </div>
            </template>
          </input-dropdown>
        </div>
      </div>
    </drawer-block>

    <drawer-block :title="$t('labels.interval')">
      <div class="line">
        <label class="label">
          {{ $t("labels.waitTimeBetweenRounds") }}{{ $t(":") }}
        </label>
      </div>
      <div class="line">
        <input-number
          :value="ctx.flow.interval"
          :min="1"
          @update:value="ctx.updateKey('interval', $event)"
        />
        <span>*</span>
        <span class="whitespace-nowrap"
          >{{ roundLength }} {{ $t(`roundUnits.${roundUnit}`) }}</span
        >
      </div>
    </drawer-block>

    <drawer-block
      :title="$t('labels.design')"
    >
      <div class="column gap-4">
        <input-switch
          v-model:value="visibleInScene"
          :label="$t('labels.visible')"
          :label-right="true"
        />
      </div>
    </drawer-block>

    <!-- DELETE -->
    <menu-group class="mt-auto">
      <button
        type="button"
        class="button button_secondary mv-2"
        @click="ctx.delete()"
      >
        {{ $t("actions.deleteFlow", 1) }}
      </button>
    </menu-group>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";

import SortableList from "@core/ui/SortableList.vue";
import DrawerBlock from "@core/ui/DrawerBlock.vue";
import MenuGroup from "@core/ui/MenuGroup.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import ButtonItem from "@core/input/ButtonItem.vue";

import InputText from "@core/input/InputText.vue";
import InputTexteditor from "@core/input/TextEditor.vue";
import InputDropdown from "@core/input/InputDropdown.vue";
import InputStateToggle from "@core/input/InputStateToggle.vue";
import InputNumber from "@core/input/InputNumber.vue";
import InputSwitch from "@core/input/InputSwitch.vue";
import EditableText from "@core/input/EditableText.vue";

import ItemHeader from "@editor/components/ItemHeader.vue";
import ExpressionInput from "@editor/items/expression/ExpressionInput.vue";
import EffectExpression from "@editor/items/effect/EffectExpression.vue";

import { useDrawerStates } from "@editor/store/editor/drawerStates.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useFlow } from "@editor/items/flow/useFlow";
import { useSettingsStore } from "@editor/store/settings.store";
import { useFlowBranchStore } from "./flowBranches.store";

import type { TargetItem } from "@editor/items/target/TargetItem";
import type { ResourceItem } from "@editor/items/resource/ResourceItem";
import type { IndexItem } from "@editor/items/index/IndexItem";
import type { FlowBranchItem } from "./FlowBranchItem";
import { useSceneFlowNodeStore, type SceneFlowNode } from "./sceneFlowNodes";
import { useSceneTree } from "../scene/sceneTree";

interface Props {
  flowId: number;
  subSelection: {
    type: string;
    id: number;
  } | null;
}
const props = withDefaults(defineProps<Props>(), {});

const emit = defineEmits<{
  (e: "select:branch", branch: FlowBranchItem | null): void;
  (e: "close"): void;
}>();

const drawerStates = useDrawerStates();
const targetStore = useTargetStore();
const resourceStore = useResourceStore();
const indexStore = useIndexStore();
const settingsStore = useSettingsStore();
const flowBranchStore = useFlowBranchStore();

const ctx = useFlow(() => props.flowId);

const sourceInputVisible = ref(false);
const sinkInputVisible = ref(false);

const descriptionField = ref<InstanceType<typeof InputTexteditor>>();

const itemsByUid = computed(() => {
  const items = [
    ...targetStore.orderedItems,
    ...resourceStore.orderedItems,
    ...indexStore.orderedItems
  ];
  const itemsByUid: Record<string, TargetItem | ResourceItem | IndexItem> = {};
  for (const item of items) {
    itemsByUid[item.uid] = item;
  }
  return itemsByUid;
});

function setReferencedElement(item: TargetItem | ResourceItem | IndexItem) {
  ctx.update({
    valueReferenceType: item.type,
    valueReferenceId: item.id
  });
}

const effectiveRoundLength = computed(() => {
  return roundLength.value * ctx.flow.interval;
});
const roundLength = computed(() => settingsStore.simulationRoundLength);
const roundUnit = computed(() => settingsStore.simulationRoundUnit);

const availableBranchElements = computed(() => {
  const elements = [...targetStore.orderedItems, ...resourceStore.orderedItems];
  return elements.filter((element) => {
    return !ctx.flowBranches.some((branch) => {
      return (
        branch.elementType == element.type && branch.elementId == element.id
      );
    });
  });
});

const selectedBranch = computed(() => {
  if (props.subSelection?.type !== "flowBranch") return null;
  return flowBranchStore.items[props.subSelection.id];
});

function selectBranch(item: FlowBranchItem | null) {
  emit("select:branch", item);
}

// function invertBranch(branch: FlowBranchItem) {
//   flowBranchStore.invertCurve({ id: branch.id });
// }

async function addBranch(
  item: TargetItem | ResourceItem,
  branchType: "source" | "sink"
) {
  await flowBranchStore.create({
    flowId: ctx.flow.id,
    elementType: item.type,
    elementId: item.id,
    branchType: branchType
  });

  sourceInputVisible.value = false;
  sinkInputVisible.value = false;
}

async function deleteBranch(branch: FlowBranchItem) {
  await flowBranchStore.delete(branch);
}

function updateFlowBranchDirections(
  flowBranchIds: number[],
  branchType: "source" | "sink",
  targetIndex: number
) {
  flowBranchStore.updateDirection(flowBranchIds, branchType, targetIndex);
}

function getElementUnit(element: TargetItem | ResourceItem | IndexItem) {
  if (!element) return "";

  if (element.unit) return element.unit;

  if (element.type == "index") {
    return indexStore.units[element.id];
  }

  return "";
}

// ====================
// SCENE VISIBILITY

const sceneTree = useSceneTree();
const sceneFlowNodeStore = useSceneFlowNodeStore();
const sceneFlowNode = computed(() => {
  return sceneFlowNodeStore.byScene[sceneTree.activeScene.id]?.[ctx.flow.id];
});
function updateSceneFlowNode<K extends keyof SceneFlowNode>(flowId: number,
  key: K,
  value: SceneFlowNode[K]
) {
  sceneFlowNodeStore.updateOrCreate({
    flowId,
    sceneId: sceneTree.activeScene.id,
    [key]: value
  });
}
const visibleInScene = computed({
  get() {
    return sceneFlowNode.value?.visible ?? true;
  },
  set(value: boolean) {
    // Make sure that the flow node is visible (not overlapped by an element) when setting it to visible.
    if (value && sceneFlowNode.value && sceneFlowNodeOverlapsElement(sceneFlowNode.value)) {
      // If overlapping, remove the place marker, this triggers the automatic repositioning of the flow node to a non-overlapping position.
      sceneFlowNodeStore.delete({ id: sceneFlowNode.value.id });
    } else {
      updateSceneFlowNode(props.flowId, "visible", value);
    }
  }
});
function sceneFlowNodeOverlapsElement(flowNode: SceneFlowNode | null | undefined) {
  if (!flowNode) return false;
  const elements = sceneTree.elementNodes.values();
  const FLOW_NODE_SIZE = 10;
  for (const elementNode of elements) {
    const node = elementNode.node;
    if (node.x < flowNode.x + FLOW_NODE_SIZE &&
        node.x + node.width > flowNode.x &&
        node.y < flowNode.y + FLOW_NODE_SIZE &&
        node.y + node.height > flowNode.y) {
      return true;
    }
  }
  return false;
}
</script>

<style lang="scss">
.flow-menu {
  display: flex;
  flex-direction: column;
  min-height: 100%;

  &__branches {
    display: flex;
    flex-direction: column;
  }

  &__branch {
    grid-column: 1 / span 4;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 4rem 1rem 2rem;
    gap: 0.5rem;
    align-items: center;
  }

  &__signbutton {
    background-color: rgba(var(--tint-contrast), 0.1);
    border: none;
    box-shadow: 0 2px 4px 0 var(--color-shadow-3);
    border-radius: 5px;
    height: 1.8rem;
    font-size: 1.25rem;
  }
}
</style>
../expression/expressionFunctions